import { useState } from "react";
import { isPersonArchiveOnly, type PersonDetailWire } from "@releve/shared";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DangerZone } from "@/components/ui/danger-zone";
import { useDeletePerson, useUpdatePerson } from "@/hooks/usePeople";
import { useTranslation } from "@/hooks/useTranslation";
import { describeArchiveReasons } from "./personFormSchema";

interface PersonDangerZoneProps {
  person: PersonDetailWire;
  /** After a successful archive (the person stays; the form closes). */
  onArchived: (person: PersonDetailWire) => void;
  /** After a successful delete — the CALLER decides where to go (never navigates here). */
  onDeleted: () => void;
  disabled?: boolean;
}

/**
 * The person form's danger zone (edit mode only). A person referenced by
 * posted or payee records, a sole-subject consent record, or one already
 * GDPR-erased can only be ARCHIVED (`status = 'inactive'`); anyone else can
 * be deleted. The switch reads `archive_only_reasons` from the detail — the
 * DELETE route re-checks and answers 409 `person_archive_only` if a
 * reference appeared meanwhile (the hook toasts it).
 */
export function PersonDangerZone({ person, onArchived, onDeleted, disabled }: PersonDangerZoneProps) {
  const { t } = useTranslation("people");
  const updatePerson = useUpdatePerson();
  const deletePerson = useDeletePerson();
  const [confirm, setConfirm] = useState<"archive" | "delete" | null>(null);

  const archiveOnly = isPersonArchiveOnly(person.archive_only_reasons);
  const erased = person.archive_only_reasons.erased;
  const alreadyArchived = !!person.status && person.status !== "active";

  if (archiveOnly) {
    const why = erased ? t("dangerZone.erased") : describeArchiveReasons(t, person);
    const description = alreadyArchived
      ? `${why} ${t("dangerZone.alreadyArchived")}`
      : `${why} ${t("dangerZone.archiveDescription")}`;
    return (
      <>
        <DangerZone
          title={t("dangerZone.title")}
          actionTitle={t("dangerZone.archiveTitle")}
          description={description}
          buttonLabel={t("dangerZone.archiveButton")}
          onAction={() => setConfirm("archive")}
          disabled={disabled || erased || alreadyArchived || updatePerson.isPending}
        />
        {confirm === "archive" && (
          <ConfirmDialog
            open
            onOpenChange={(open) => !open && setConfirm(null)}
            title={t("dangerZone.archiveConfirmTitle", { name: person.full_name })}
            description={t("dangerZone.archiveConfirmDescription")}
            confirmText={t("dangerZone.archiveButton")}
            cancelText={t("common:cancel")}
            variant="destructive"
            onConfirm={async () => {
              try {
                const archived = await updatePerson.mutateAsync({ id: person.id, status: "inactive" });
                onArchived(archived);
              } catch {
                // useUpdatePerson toasts the failure; the MutationCache reports it.
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <DangerZone
        title={t("dangerZone.title")}
        actionTitle={t("dangerZone.deleteTitle")}
        description={t("dangerZone.deleteDescription")}
        buttonLabel={t("dangerZone.deleteButton")}
        onAction={() => setConfirm("delete")}
        disabled={disabled || deletePerson.isPending}
      />
      {confirm === "delete" && (
        <ConfirmDialog
          open
          onOpenChange={(open) => !open && setConfirm(null)}
          title={t("dangerZone.deleteConfirmTitle", { name: person.full_name })}
          description={t("dangerZone.deleteConfirmDescription")}
          confirmText={t("dangerZone.deleteButton")}
          cancelText={t("common:cancel")}
          variant="destructive"
          onConfirm={async () => {
            try {
              await deletePerson.mutateAsync(person.id);
              onDeleted();
            } catch {
              // useDeletePerson toasts the failure (incl. a late
              // `person_archive_only` refusal); the MutationCache reports it.
            }
          }}
        />
      )}
    </>
  );
}
